"use client";
import { FC, ReactNode, cloneElement, isValidElement, useEffect, useRef } from "react";

interface VisibilitySensorProps {
  onVisible: (isVisible: boolean, entry: IntersectionObserverEntry) => void;
  children: ReactNode;
  onlyOnce?: boolean;
}

const VisibilitySensor: FC<VisibilitySensorProps> = (props) => {
  const { onVisible, children, onlyOnce = true } = props;

  const onVisibleRef = useRef(onVisible);
  const componentRef = useRef(null);
  const onlyOnceRef = useRef(!!onlyOnce);

  useEffect(() => {
    onVisibleRef.current = onVisible;
  }, [onVisible]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (typeof onVisibleRef.current === "function") {
          onVisibleRef?.current(!!entry?.isIntersecting, entry);
        }
        if (onlyOnceRef?.current && componentRef?.current && !!entry?.isIntersecting) {
          observer?.unobserve(componentRef?.current);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    if (componentRef?.current) {
      observer?.observe(componentRef?.current);
    }
    return () => {
      observer?.disconnect();
    };
  }, []);

  return isValidElement(children) ? cloneElement(children, { ref: componentRef } as any) : children;
};

export default VisibilitySensor;
